import React, { useState } from 'react';
import axios from 'axios';
import { Mail, Loader2, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { formatApiErrorDetail } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export const ResendInviteButton = ({ endpoint, recipient, staff = false, onResent, testId }) => {
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const link = result?.setup_url || result?.invite_url || '';

  const resend = async () => {
    setSending(true);
    setError('');
    try {
      const { data } = await axios.post(`${API}${endpoint}`, {}, { withCredentials: true });
      setResult(data);
      if (data.email_sent) toast.success(`Invitation sent to ${recipient}`);
      else toast.warning('Email was not sent. You can copy the new link below.');
      onResent?.();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(link);
      toast.success('Invitation link copied');
    } catch (e) {
      toast.error('Could not copy. Select and copy the link from the field.');
    }
  };

  return (
    <>
      <Button variant="outline" size="sm" data-testid={testId}
        onClick={() => { setResult(null); setError(''); setOpen(true); }}>
        <Mail className="w-4 h-4 mr-2" aria-hidden="true" /> Resend Invite
      </Button>
      <Dialog open={open} onOpenChange={(value) => { if (!sending) setOpen(value); }}>
        <DialogContent className="w-[calc(100vw_-_2rem)] max-w-lg" data-testid={`${testId}-dialog`}>
          <DialogHeader>
            <DialogTitle>{staff ? 'Resend Portal Invitation' : 'Resend Client Profile Invitation'}</DialogTitle>
            <DialogDescription className="break-words" data-testid={`${testId}-description`}>
              Send a fresh {staff ? 'account setup' : 'Client Profile'} link to {recipient}.
              The previous link will stop working. The new link expires in {staff ? '24 hours' : '14 days'}.
              {!staff && ' Any saved profile progress will be kept.'}
            </DialogDescription>
          </DialogHeader>
          {error && <p role="alert" className="text-sm text-red-600" data-testid={`${testId}-error`}>{error}</p>}
          {result ? (
            <div className="space-y-3">
              <p role="status" className="text-sm text-[#0f172a] break-words" data-testid={`${testId}-result`}>
                {result.email_sent ? `Invitation sent to ${recipient}.` : `Email was not sent: ${result.email_error || 'Email delivery is unavailable.'} Share the new link manually.`}
              </p>
              <Input value={link} readOnly aria-label="New invitation link" data-testid={`${testId}-link`} />
              <div className="flex flex-wrap justify-end gap-2">
                <Button variant="outline" onClick={copyLink} data-testid={`${testId}-copy`}><Copy className="w-4 h-4 mr-2" />Copy Link</Button>
                <Button onClick={() => setOpen(false)} data-testid={`${testId}-done`}>Done</Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap justify-end gap-2">
              <Button variant="outline" disabled={sending} onClick={() => setOpen(false)} data-testid={`${testId}-cancel`}>Cancel</Button>
              <Button disabled={sending} onClick={resend} className="bg-[#26518c] hover:bg-[#1e3a63] text-white" data-testid={`${testId}-confirm`}>
                {sending && <Loader2 className="w-4 h-4 mr-2 animate-spin" aria-hidden="true" />}
                {sending ? 'Sending…' : 'Resend Invite'}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
